import { observable, computed } from "mobx";
import { RacecardLineInfo } from ".././RacecardLineInfo";
import { RacingTrackInfo } from ".././RacingTrackInfo";
import { QALineInfo, DataHelper } from "sb-resp-lib";
import { CastSelectionPlace, CastPositions } from "../CastSelectionPlace";

export enum RacingCastMode {
    Empty = 0,
    OnePlace = 1,
    AnyPlace = 2
};

/**
 * RacingEventInfo defines event for racing braches.
 *
 * @typedef RacingEventInfo
 * @type {Object}
 * @property {id} Unique identifier
 * @property {eventName} Name of the event
 * @property {typeId} type of the event (RaceCard,TrapMarket etc.)
 * @property {date} Date of event
 * @property {lines} Hashtable of QALines
 * @property {racecardInfo}  Conditions of the track
 * @property {date} Date of event
 * @property {horseRacingStateId} Determines of the event is SP or DOE
 * @property {raceProgress} Determines if the events is started, finished
 * @property {forecastEnabled} Determines if forecast is enabled for event
 * @property {tricastEnabled}  Determines if tricast is enabled for event
 * @property {forecastMaxBet} Forecast max bet for the event
 * @property {branchId} brachId for the event
 * @property {feedEventId} the EventId provided by the feed
 * @property {urlCode} used for SEO navigation
 * @property {racecardLines} Hashtable of RacecardLineInfo
 * @property {racingTrackInfo} Contains additional information for the event
 * @property {trackConditions}  Conditions of the track
 * @property {forecastSelections} Forecast selections for the current event
 * @property {tricastSelections} Tricast selections for the current event
 */

/* istanbul ignore next */
export type RacingEventInfoType = {
    id: string;
    eventName: string;
    lines: QALineInfo[];
    typeId: string;
    date: Date;
    leagueId: string;
    isEachWayEnabled: boolean;
    ewPlaceTerms?: string;
    ewOddsTerms?: string;
    hasLiveStream: boolean;
    racingStateId: number;
    raceProgress: number;
    tricastEnabled: boolean;
    forecastEnabled: boolean;
    forecastMaxBet: number;
    branchId: string;
    feedEventId: string;
    masterLeagueId: string;
    feedProviderId: string;
    urlCode: string;
    racecardLines: RacecardLineInfo[];
    racingTrackInfo?: RacingTrackInfo;
    trackCondition?: string;
}
/* istanbul ignore next */
export class RacingEventInfo {
    id: string;
    eventName: string;
    @observable lines: QALineInfo[];
    typeId: string;
    date: Date;
    leagueId: string;
    branchId: string;
    feedEventId: string;
    feedProviderId: string;
    masterLeagueId: string;
    urlCode: string;
    @observable isEachWayEnabled?: boolean;
    @observable ewPlaceTerms?: string;
    @observable ewOddsTerms?: string;
    @observable hasLiveStream: boolean;
    @observable racingStateId: number;
    @observable raceProgress: number;
    @observable tricastEnabled: boolean;
    @observable forecastEnabled: boolean;
    @observable forecastMaxBet: number;
    @observable racecardLines: RacecardLineInfo[];
    @observable racingTrackInfo?: RacingTrackInfo;
    @observable trackCondition?: string;
    @observable isSortedAsc: boolean
    @observable forecastSelections: CastSelectionPlace[];
    @observable tricastSelections: CastSelectionPlace[];
    @observable tricastMode: RacingCastMode;
    @observable forecastMode: RacingCastMode;

    constructor(source: RacingEventInfoType) {
        this.id = source.id;
        this.date = source.date;
        this.eventName = source.eventName;
        this.lines = source.lines;
        this.typeId = source.typeId;
        this.leagueId = source.leagueId;
        this.isEachWayEnabled = source.isEachWayEnabled;
        this.ewPlaceTerms = source.ewPlaceTerms;
        this.ewOddsTerms = source.ewOddsTerms;
        this.hasLiveStream = source.hasLiveStream;
        this.racingStateId = source.racingStateId;
        this.raceProgress = source.raceProgress;
        this.tricastEnabled = source.tricastEnabled;
        this.forecastEnabled = source.forecastEnabled;
        this.forecastMaxBet = source.forecastMaxBet;
        this.branchId = source.branchId;
        this.masterLeagueId = source.masterLeagueId;
        this.feedEventId = source.feedEventId;
        this.urlCode = source.urlCode;
        this.feedProviderId = source.feedProviderId;
        this.racecardLines = source.racecardLines;
        this.racingTrackInfo = source.racingTrackInfo;
        this.trackCondition = source.trackCondition;
        this.isSortedAsc = true;
        this.forecastSelections = [];
        this.tricastSelections = [];
        this.tricastMode = RacingCastMode.Empty;
        this.forecastMode = RacingCastMode.Empty;
    }

    @computed get sortedLinesByNumber() {
        let ref = this;
        let res = this.lines.slice();

        /* tslint:disable:no-null-keyword  no-non-null-assertion */
        res.sort(DataHelper.chainSort(
            function (l1, l2) { return DataHelper.compareByLineTypeID(l1, l2); },
            function (l1, l2) { return DataHelper.compareByLineStateID(l1, l2); },
            /* istanbul ignore next */
            function (l1, l2) {
                let rc1 = ref.racecardLines ? ref.racecardLines[l1.id] : null;
                /* istanbul ignore next */
                let rc2 = ref.racecardLines ? ref.racecardLines[l2.id] : null;
                /* istanbul ignore next */
                if (!(rc1 && rc2)) { return 0; }
                /* istanbul ignore next */
                return ref.isSortedAsc ?
                    DataHelper.compare(rc1.competitorNumber, rc2.competitorNumber) :
                    - DataHelper.compare(rc1.competitorNumber, rc2.competitorNumber);
            }
        ));
        /* tslint:enable:no-null-keyword  no-non-null-assertion */
        return res;
    }

    @computed get sortedLinesByName() {
        let ref = this;
        let res = this.lines.slice();

        res.sort(DataHelper.chainSort(
            function (l1, l2) { return DataHelper.compareByLineTypeID(l1, l2); },
            function (l1, l2) { return DataHelper.compareByLineStateID(l1, l2); },
            function (l1, l2) {
                let result = 0;
                if (l1.lineName > l2.lineName) { result = 1; }

                ref.isSortedAsc ? result : -result;
            }
        ));
        return res;
    }

    @computed get sortedLinesByOdds() {
        let ref = this;
        let res = this.lines.slice();

        res.sort(DataHelper.chainSort(
            function (l1, l2) { return DataHelper.compareByLineTypeID(l1, l2); },
            function (l1, l2) { return DataHelper.compareByLineStateID(l1, l2); },
            function (l1, l2) {
                //Sort by SP or not
                var l1sp = (l1.lineTypeId === 24 && l1.odds === 0 ? 1 : 0);
                var l2sp = (l2.lineTypeId === 24 && l2.odds === 0 ? 1 : 0);
                /* istanbul ignore next */
                return DataHelper.compare(l1sp, l2sp);
            },
            function (l1, l2) { return ref.isSortedAsc ? DataHelper.compare(l1.odds, l2.odds) : -DataHelper.compare(l1.odds, l2.odds); },
            function (l1, l2) {
                //Sort by horse nbr (if available)
                var rc1 = ref.racecardLines.find((line) => line.lineId === l1.id);
                var rc2 = ref.racecardLines.find((line) => line.lineId === l2.id);
                if (!rc1 || !rc2) { return 0; }
                return ref.isSortedAsc ? DataHelper.compare(rc1.competitorNumber, rc2.competitorNumber) : -DataHelper.compare(rc1.competitorNumber, rc2.competitorNumber);
            }

        ));
        return res;
    }

    /* istanbul ignore next */
    @computed get formattedDate() {
        /* istanbul ignore next */
        return this.date.formatDate("dd/MM");
    }

    /* istanbul ignore next */
    @computed get formattedTime() {
        /* istanbul ignore next */
        return this.date.formatDate("HH:mm");
    }

    getCastSelectionProp(isTricast: boolean): CastSelectionPlace[] {
        if (isTricast) {
            return this.tricastSelections;
        }

        return this.forecastSelections;
    }
    getCastModeProp(isTricast: boolean): RacingCastMode {
        if (isTricast) {
            return this.tricastMode;
        }

        return this.forecastMode;
    }
    setCastModeValue(isTricast: boolean, value: RacingCastMode) {
        if (isTricast) {
            this.tricastMode = value;
        }
        else {
            this.forecastMode = value;
        }
    }

    getIsLineDisabled(position: CastPositions, isTricast: boolean) {
        let eventStatus = this.getCastModeProp(isTricast);
        switch (position) {
            case CastPositions.first:
            case CastPositions.second:
            case CastPositions.third:
                {

                    return eventStatus === RacingCastMode.AnyPlace;
                }
            case CastPositions.any:
                {
                    return eventStatus === RacingCastMode.OnePlace;
                }
        }

    }
}